import { useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";

/**
 * The "modal over list, with deep-linkable URL" routing pattern.
 *
 * When a user clicks a list item that should open as a modal, we push a new
 * URL (e.g. `/calendar/<slug>`) but stash the original list location in
 * `location.state.backgroundLocation`. The top-level `App.tsx` renders the
 * main route tree against the *background* location (so the list stays
 * mounted), then renders an additional `<Routes>` block for the modal route
 * that overlays it. On refresh / direct visit / paste, `state` is absent,
 * `background` is null, and the route renders as a full page.
 *
 * This file is the single source of truth for that convention.
 */

interface ModalLocationState {
  backgroundLocation?: Location;
}

/** Returns the background location if the current navigation was opened as a
 *  modal (i.e. via `state={modalLinkState(loc)}`), else null. */
export function useBackgroundLocation(): Location | null {
  const location = useLocation();
  const state = location.state as ModalLocationState | null;
  return state?.backgroundLocation ?? null;
}

/** Build the `state` object to pass to a `<Link>` so it opens as a modal. */
export function modalLinkState(currentLocation: Location): ModalLocationState {
  return { backgroundLocation: currentLocation };
}
